import React, { ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import useStyles from '../../styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import {
  AppSelect,
  AppTextField,
} from '../../../../../../components/molecules/form_controls';
import GridLayout from '../../../../../../components/molecules/grid_layout';
import { getState } from '../../../../../../redux/store';
import { ReducerKeys } from '../../../../../../redux/reducers';
import RegistrationDetailActions from '../../../../../../redux/reducers/registration_detail_reducer/actions';
import * as RegistrationDetailSelectors from 'redux/reducers/registration_detail_reducer/selectors';
import TranslatedText from '../../../../../../components/atoms/translated_text';
import { Translations } from '../.././configuration';
import ModalActions from '../../../../../../redux/reducers/gl_modal_reducer/actions';
import { ModalIDs } from '../../../../../../redux/reducers/gl_modal_reducer/types';
import { Button } from '@material-ui/core';
import { PreviousUseOfSpecialSchemeInput } from '../../model';
import { isVatNumberValid } from 'utils/general';

interface FormFields {
  issuingCountry: string | null;
  schemeType: string | null;
  vatNum: string | null;
}

export default function PreviousUseOfSpecialSchemesForm(): ReactElement {
  const dispatch = useDispatch();
  const defaultValues: FormFields = {
    issuingCountry: null,
    schemeType: null,
    vatNum: null,
  };
  // console.log('Render PreviousUseOfSpecialSchemes');

  const { errors, control, handleSubmit, setError } = useForm<FormFields>({
    defaultValues,
  });
  const classes = useStyles();

  const onSubmit = (data: any) => {
    if (!isVatNumberValid(data.vatNum)) {
      setError('vatNum', {
        type: 'manual',
        message: `This is an invalid VAT Number`,
      });
      return;
    }

    let reduxData = getState(
      ReducerKeys.REGISTRATION_DETAIL_REDUCER
    ).previousUseOfSpecialSchemes;
    let newData: Array<PreviousUseOfSpecialSchemeInput> =
      new Array<PreviousUseOfSpecialSchemeInput>();
    let countries = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
      .memberStateCountries.data;

    for (let item of reduxData) newData.push(item);
    let newPUOSS: PreviousUseOfSpecialSchemeInput = {
      id: 0,
      applicant_type_id: data.schemeType,
      vat_id_num: data.vatNum,
      country_id:
        countries[Number.parseInt(data.issuingCountry) - 1].country_id,
      country: countries[Number.parseInt(data.issuingCountry) - 1].description,
    };
    newData.push(newPUOSS);

    dispatch(RegistrationDetailActions.setPreviousUseOfSpecialScheme(newData));
    dispatch(
      ModalActions.closeModalAction({
        modalID: ModalIDs.MODAL_PREVIOUS_USE_OF_SPECIAL_SCHEMES,
      })
    );
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={[
          {
            element: renderIssuingCountryField(errors, control),
            size: 12,
            id: 'issuingCountry',
          },
          {
            element: renderSchemeTypeField(errors, control),
            size: 12,
            id: 'schemeType',
          },
          {
            element: renderVatNumberField(errors, control),
            size: 12,
            id: 'vatNum',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ width: '100%' }}
              >
                <TranslatedText textMap={Translations.add_special_scheme} />
              </Button>
            ),
            size: 12,
            id: 'action',
          },
        ]}
      />
    </form>
  );
}

const renderIssuingCountryField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  // let countries = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
  //   .nonMemberStateCountries.data;
  let countries = RegistrationDetailSelectors.useGetCountriesMemberState();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: Translations.issuing_country_label.English,
  });
  if (countries && countries.length > 0)
    countries.map((country: any, index: number) => {
      select_countries.push({
        id: index + 1,
        label: country.description,
      });
    });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="issuingCountry"
      label={<TranslatedText textMap={Translations.issuing_country_label} />}
      error={'issuingCountry' in errors}
      name="issuingCountry"
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      helperText={errors.issuingCountry && errors.issuingCountry.message}
    />
  );
};

const renderSchemeTypeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  // const types = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
  //   .schemeTypesList.data;
  let types = RegistrationDetailSelectors.useGetSchemeTypes();
  let types_options = [];
  types_options.push({
    id: null,
    label: Translations.scheme_type_label.English,
  });
  if (types && types.length > 0)
    for (let type of types)
      types_options.push({
        id: type.applicant_type_id,
        label: type.description,
      });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="schemeType"
      label={<TranslatedText textMap={Translations.scheme_type_label} />}
      error={'schemeType' in errors}
      name="schemeType"
      control={control}
      rules={{ required: 'Required Field' }}
      options={types_options as any}
      helperText={errors.schemeType && errors.schemeType.message}
    />
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="vatNum"
      label={<TranslatedText textMap={Translations.vat_number_label} />}
      error={'vatNum' in errors}
      name="vatNum"
      control={control}
      rules={{
        required: 'Required Field',
        maxLength: { value: 12, message: 'Maximum length 12' },
      }}
      helperText={errors.vatNum && errors.vatNum.message}
    />
  );
};
